import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Col, Divider, Row } from 'antd';
import { klona } from 'klona';
import dayjs from 'dayjs';
import { EditMemberInfoScene, PhysicalInfoDimension, TrackerCustomEvent, useTracker } from 'common-package';
import { useNavigate } from 'react-router';
import defaultAvatarImg from '@/assets/image/default-avatar.png';
import defaultFemaleAvatarImg from '@/assets/image/default-women-avatar.png';
import VipIcon from '@/assets/icon/vip.svg';
import MemberDrawerComp from '@/components/member-drawer';
import { ALL_PAGES, GenderLabel } from '@/constant';
import { selectedStoreInfo } from '@/store/device';
import { maskPhoneNumber, maskPhoneNumberInterNational, showBirthday } from '@/utils';
import { BACK_AREA, SHOULDER_NECK_AREA } from '@/pages/ai-consultation/components/result-plan';
import { POSTURE_ADJUST_ITEM_2_TEXT } from '@/pages/ai-consultation/components/posture-condition';
import { PAIN_LEVEL_2_TEXT } from '@/pages/ai-consultation/components/pain-condition-level';
import { PHYSICAL_CONDITION_ITEMS } from '@/pages/ai-consultation/components/physical-condition';
import { findMatchingElements, THERAPY_TYPE_SORT } from '../member-physiotherapy-info/ExpandContent/DetectionResult';
import { formatNumber } from '../../member-management/components/AIConsultationInfo/components/WeightLoss/WeightLossItem';
import { DIMENSION_TYPE_SORT } from '../../member-management/components/AIConsultationInfo';
import cx from './index.module.scss';

interface MemberDrawerProps {
  open: boolean;
  memberInfo: GetCustomerProfileResponse;
  handleClose?: () => void;
  isServicing?: boolean;
}

const THERAPY_TITLE: Record<AIDiagnose.TherapyType, string> = {
  weightLoss: '减肥瘦身',
  bodyAdjust: '体态调整',
  painRelief: '疼痛舒缓',
  physicalImprove: '体质调理',
};

const MemberDrawer = ({ open, memberInfo, handleClose, isServicing = false }: MemberDrawerProps) => {
  const {
    avatarUrl,
    name,
    age,
    gender,
    birthday,
    phoneNumber,
    servedParts,
    lastVisitTime,
    visits,
    height,
    weight,
    bodyFatRate,
    hopeTypes,
  } = memberInfo || {};
  const storeInfo = useSelector(selectedStoreInfo);
  const { sendEvent } = useTracker();
  const nav = useNavigate();
  const day = lastVisitTime?.split(' ')[0];
  const time = lastVisitTime?.split(' ')[1];

  // 融合方案排序
  const hopeTypesSort = useMemo(() => {
    const newHopeTypes = klona(hopeTypes);
    return newHopeTypes?.sort((a, b) => THERAPY_TYPE_SORT?.indexOf(a) - THERAPY_TYPE_SORT?.indexOf(b)) || [];
  }, [hopeTypes]);

  // 展示痛症舒缓选中区域
  const currentMarkPainAreaShowName = useMemo(
    () =>
      Array.from(
        new Set(
          memberInfo?.painReliefList?.map(item =>
            SHOULDER_NECK_AREA.includes(item.problem) ? '肩部' : BACK_AREA.includes(item?.problem) ? '背部' : '腰部'
          )
        )
      )
        ?.sort((a, b) => ['肩部', '背部', '腰部']?.indexOf(a) - ['肩部', '背部', '腰部']?.indexOf(b))
        ?.join('｜') || [],
    [memberInfo?.painReliefList]
  );

  const currentMarkBodyAdjust = useMemo(
    () =>
      Array.from(new Set(memberInfo?.bodyAdjustList))
        ?.sort(
          (a, b) =>
            Object.keys(POSTURE_ADJUST_ITEM_2_TEXT)?.indexOf(a?.problem) -
            Object.keys(POSTURE_ADJUST_ITEM_2_TEXT)?.indexOf(b?.problem)
        )
        ?.map(item => PAIN_LEVEL_2_TEXT[item?.level] + POSTURE_ADJUST_ITEM_2_TEXT[item?.problem])
        ?.join('｜'),
    [memberInfo?.bodyAdjustList]
  );

  const currentMarkPhysicalImprove = useMemo(
    () =>
      Array.from(new Set(memberInfo?.physicalImproveList))
        ?.sort(
          (a, b) =>
            Object.keys(PHYSICAL_CONDITION_ITEMS)?.indexOf(a?.problem) -
            Object.keys(PHYSICAL_CONDITION_ITEMS)?.indexOf(b?.problem)
        )
        ?.filter(
          item =>
            //@ts-ignore
            item.problem !== 'stayUpLate'
        )
        ?.map(item => `${PAIN_LEVEL_2_TEXT[item?.level]}${PHYSICAL_CONDITION_ITEMS[item.problem]?.name}`)
        ?.join('｜'),
    [memberInfo?.physicalImproveList]
  );

  const currentWeightLoss = useMemo(() => {
    const newProblems = klona(memberInfo?.weightLossList);
    newProblems?.sort((a, b) => DIMENSION_TYPE_SORT?.indexOf(a?.problem) - DIMENSION_TYPE_SORT?.indexOf(b?.problem)) ||
      [];

    return (
      newProblems
        ?.map(
          item =>
            `目标${PhysicalInfoDimension[item?.problem]}${
              item?.goalValue ? ` ${formatNumber(item?.goalValue)}厘米` : ` 暂无`
            }` +
            `｜当前${PhysicalInfoDimension[item?.problem]}${
              item?.currentValue ? ` ${formatNumber(item?.currentValue)}厘米` : ` 暂无`
            }`
        )
        ?.join('｜') || []
    );
  }, [memberInfo?.weightLossList]);

  return (
    <MemberDrawerComp
      open={open}
      handleClose={handleClose}
      width={512}
      maskClosable={true}
      mask={true}
      drawerStyle={{ padding: '0 32px 40px 32px' }}
      maskStyle={{ background: 'none' }}
    >
      <div className={cx('wrapper')}>
        <div className={cx('avatar')}>
          <img
            src={avatarUrl ? avatarUrl : gender === 'Female' ? defaultFemaleAvatarImg : defaultAvatarImg}
            alt="avatar"
          />
          <div className={cx('vip-logo')}>
            <img src={VipIcon} alt="vip" />
          </div>
        </div>

        <div className={cx('name')}>
          {name} <span className={cx('divider')}>|</span> {showBirthday(age, birthday)}
        </div>

        <div className={cx('member-info')}>
          <Row justify="space-between">
            <Col span={12}>
              <div className={cx('desc-line')}>
                <span className={cx('title')}>手机号码</span>
                <span className={cx('content')}>
                  {storeInfo?.phoneNumberCheck === 1
                    ? maskPhoneNumber(phoneNumber || '')
                    : maskPhoneNumberInterNational(phoneNumber || '')}
                </span>
              </div>

              <div className={cx('desc-line')}>
                <span className={cx('title')}>出生日期</span>
                <span className={cx('content')}>{birthday ? dayjs(birthday).format('YYYY年MM月DD日') : '-'}</span>
              </div>
              <div className={cx('desc-line')}>
                <span className={cx('title')}>会员性别</span>
                {/* 如果为空显示女 */}
                <span className={cx('content')}>{GenderLabel[memberInfo?.gender] || '女'}</span>
              </div>
            </Col>
            <Col span={12}>
              <div className={cx('desc-line')}>
                <span className={cx('title')}>来访次数</span>
                <span className={cx('visit', 'content')}>{visits}次</span>
              </div>
              <div className={cx('desc-line')}>
                <span className={cx('title')}>最近来访</span>
                <span className={cx('content')}>
                  {day} <Divider type="vertical" style={{ display: day && time ? 'block' : 'none' }} />
                  {time}
                </span>
              </div>
            </Col>
            <Col span={24}>
              <div className={cx('desc-line')}>
                <span className={cx('title')}>操作部位</span>
                <span className={cx('content')}>{(servedParts || []).join('、')}</span>
              </div>
            </Col>
            <Col span={24}>
              <div className={cx('desc-line')}>
                <span className={cx('title')}>身体数据</span>

                {height || weight || bodyFatRate ? (
                  <span className={cx('content')}>
                    <div className={cx('text')}>
                      <span>身高 </span>
                      <span>{height}厘米</span>
                      <span>｜</span>
                      <span>体重 </span>
                      <span>{weight}公斤</span>
                      <span>｜</span>
                      <span>体脂率 </span>
                      <span>{bodyFatRate}%</span>
                    </div>
                  </span>
                ) : (
                  <div className={cx('content')}>待完善</div>
                )}
              </div>
            </Col>

            <Col span={!!hopeTypes?.length ? 0 : 24}>
              <div className={cx('desc-line')}>
                <span className={cx('title')}>身体情况</span>
                <span className={cx('content')}>{(memberInfo?.physicalAssessment || []).join('、') || '待完善'}</span>
              </div>
            </Col>
            <Col span={!!hopeTypes?.length ? 0 : 24}>
              <div className={cx('desc-line')}>
                <span className={cx('title')}>理疗诉求</span>
                <span className={cx('content')}>{(memberInfo?.adjustmentPurpose || []).join('、') || '待完善'}</span>
              </div>
            </Col>
            <Col span={!!hopeTypes?.length ? 24 : 0}>
              <div className={cx('desc-line')}>
                <span className={cx('title')}>身体情况</span>
                {hopeTypes?.length > 0 ? (
                  <div className={cx('physical-wrapper')}>
                    {!!memberInfo?.physicalAssessment?.length && (
                      <div className={cx('problem-item')}>
                        <div className={cx('label')}>{'您的体质 |'}</div>
                        <div className={cx('value')}>
                          {(
                            (memberInfo?.tongueDiagnose
                              ? memberInfo?.physicalAssessment
                              : findMatchingElements(memberInfo?.physicalAssessment)) || []
                          ).join('｜')}
                        </div>
                      </div>
                    )}

                    {hopeTypesSort?.map(item => (
                      <div className={cx('problem-item')} key={item}>
                        <div className={cx('label')}>{THERAPY_TITLE[item] + ' |'}</div>
                        <div className={cx('value')}>
                          {item === 'painRelief'
                            ? currentMarkPainAreaShowName
                            : item === 'bodyAdjust'
                            ? currentMarkBodyAdjust
                            : item === 'weightLoss'
                            ? currentWeightLoss
                            : item === 'physicalImprove'
                            ? currentMarkPhysicalImprove
                            : null}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className={cx('content')}>{(memberInfo?.adjustmentPurpose || []).join('、') || '待完善'}</span>
                )}
              </div>
            </Col>

            <Col span={24}>
              <div className={cx('desc-line')}>
                <span className={cx('title')}>更多备注</span>
                <span className={cx('content')}>{memberInfo?.remark || '待完善'}</span>
              </div>
            </Col>
          </Row>
        </div>
      </div>
      <div
        className={cx('perfect-data-btn', { hide: isServicing })}
        onClick={() => {
          sendEvent?.(TrackerCustomEvent.EditMemberInfoClk, { et: 'CLK', c1: EditMemberInfoScene.MemberDetail });
          nav(ALL_PAGES.EDIT_MEMBER, { state: { action: 'edit', customerId: memberInfo.customerId } });
        }}
      >
        完善资料
      </div>
    </MemberDrawerComp>
  );
};

export { MemberDrawer };
